/**
 * LE PIED ET L'ACCUEIL, DÉRIVÉS DU REGISTRE — tâche C3 (C13, R5).
 *
 * Trois fonctions PURES. Elles ne lisent aucun fichier de module : elles
 * filtrent ce que les modules ont DÉCLARÉ dans leur `ModuleDef` (`lienPied`,
 * `accueil`). C'est la règle D8 du cahier des charges — le noyau n'importe
 * jamais un fichier interne d'un module, sinon le module cesse d'être
 * déplaçable. Shell et App consomment ces trois fonctions et rien d'autre.
 *
 * Pures aussi pour une raison de test : le registre réel vient d'un
 * `import.meta.glob`, impossible à paramétrer. En passant la liste de modules
 * en argument, `accueil.test.ts` les exerce sur des modules factices — donc on
 * teste la RÈGLE, pas l'état du dossier `modules/` du jour.
 */
import type { Role } from "@geceram/shared";
import type { ModuleDef } from "./module-contract";
import { canAccess } from "./module-contract";

/**
 * Les modules qui captent l'entrée, dans l'ordre du registre (= l'ordre visuel
 * de la nav). Le rôle filtre AVANT tout : un module réservé ne peut pas
 * détourner l'accueil de quelqu'un qui n'a pas le droit de l'ouvrir.
 */
export function modulesAccueil(modules: readonly ModuleDef[], roles: readonly Role[]): ModuleDef[] {
  return modules.filter((m) => canAccess(m, roles) && m.accueil !== undefined);
}

/** Les modules qui vivent dans le pied de la coque, même filtre par rôle. */
export function liensPied(modules: readonly ModuleDef[], roles: readonly Role[]): ModuleDef[] {
  return modules.filter((m) => canAccess(m, roles) && m.lienPied === true);
}

/**
 * LE DÉLAI MAXIMUM D'UNE QUESTION D'ACCUEIL.
 *
 * 5 s : au-delà, on n'attend plus une page, on regarde un écran figé. Le CRM
 * s'ouvre alors normalement — la question n'aura simplement pas eu de réponse.
 */
export const DELAI_ACCUEIL_MS = 5_000;

/**
 * LA BORNE — sans elle, `destinationAccueil` ne tient sa promesse qu'à moitié.
 *
 * Elle couvre l'échec FRANC (503, réseau coupé) : erreur, donc accueil normal.
 * Elle ne couvrait pas le SILENCE — une `queryFn` qui ne répond jamais laissait
 * « Ouverture… » à l'écran indéfiniment, sans erreur et sans limite. Une panne
 * bruyante ouvrait la porte ; une panne muette la fermait.
 *
 * ⚠ LA BORNE VIT DANS LE NOYAU, PAS DANS CHAQUE MODULE. C'est le noyau qui
 * promet « une page qui ne répond pas ne bloque jamais l'entrée » : il ne peut
 * pas tenir cette promesse en la sous-traitant à chaque module, il suffirait
 * d'un module distrait pour la perdre. La règle D8 tient quand même — cette
 * enveloppe traite TOUTES les `queryFn` de la même façon, sans rien savoir de
 * ce qu'elles interrogent.
 *
 * Le rejet est une erreur ordinaire : `destinationAccueil(…, erreur = true, …)`
 * la route déjà vers l'accueil normal, il n'y a pas de second chemin à écrire.
 * Le minuteur est TOUJOURS nettoyé — sinon chaque entrée laisserait un timer de
 * 5 s derrière elle (vérifié par `vi.getTimerCount()` dans les tests).
 */
export function avecDelai<T>(promesse: Promise<T>, ms: number = DELAI_ACCUEIL_MS): Promise<T> {
  let minuteur: ReturnType<typeof setTimeout> | undefined;
  const retard = new Promise<never>((_resoudre, rejeter) => {
    minuteur = setTimeout(() => rejeter(new Error("accueil : délai dépassé")), ms);
  });
  return Promise.race([promesse, retard]).finally(() => {
    if (minuteur !== undefined) clearTimeout(minuteur);
  });
}

/**
 * LA DÉCISION D'ENTRÉE, séparée du composant.
 *
 * `Home` appelle ses hooks sans condition, puis délègue ici : la règle se teste
 * alors sans routeur, sans QueryClient et sans rendu. C'est ce qui rend
 * vérifiable le cas qui compte le plus — celui de la panne.
 *
 * ⚠ `erreur` gagne sur tout : une lecture ratée (503 sur le journal d'audit,
 * réseau coupé) vaut un accueil NORMAL, jamais une attente ni un écran fermé.
 * Faire dépendre l'entrée dans le CRM de la santé d'un service annexe
 * transformerait une panne mineure en arrêt de travail.
 *
 * - `reponse === undefined` (et pas d'erreur) : la réponse n'est pas arrivée →
 *   on ATTEND. Sans cette attente, on partirait sur la première route montée et
 *   la redirection arriverait sur un écran déjà démonté.
 * - `reponse` est un chemin : on y va.
 * - `reponse === null` : accueil normal.
 *
 * `vers: null` signifie « ne navigue nulle part » — l'appelant montre alors son
 * écran vide ; on ne navigue jamais vers `null`.
 */
export function destinationAccueil(
  reponse: string | null | undefined,
  erreur: boolean,
  premiereRoute: string | null,
): { attendre: boolean; vers: string | null } {
  if (erreur) return { attendre: false, vers: premiereRoute };
  if (reponse === undefined) return { attendre: true, vers: null };
  return { attendre: false, vers: reponse ?? premiereRoute };
}
